import React, { useEffect, useState } from 'react';
import { PropTypes } from 'prop-types';
import { isEqual, cloneDeep } from 'lodash';
import Button from '../Button/Button';
import { createContact, getContact, updateContact } from '../../utils/api/contact';
import { addContactsToSite } from '../../utils/api/site';
import CustomFieldsForm from '../FormInputs/CustomFieldsForm';
import MainInputs from './MainInputs';
import { formatCustomFieldLabel } from '../../utils/formatCustomFields';
import { useCustomFieldErrorScroll } from '../../hooks';

// Helper function maps and prepares custom fields for consistent form rendering and submission
function parseCustomFields(initialModelFormItems, customFields) {
  let updatedCustomFields = initialModelFormItems.map((item, index) => {
    const matchingField = customFields.find((field) => field.key === item.key);
    return {
      key: item.key,
      type: item.type,
      label: formatCustomFieldLabel(item.label),
      position: index + 1,
      value: matchingField ? matchingField.value : '',
      form_item_id: item.key,
      values: item.values || [],
      required: item.required,
    };
  });
  if (customFields && customFields.length > 0) {
    updatedCustomFields = customFields
      .map((customField) => {
        const match = initialModelFormItems.find((item) => item.key === customField.key);
        let value;
        if (customField.type === 'checkbox-group') {
          if (customField.values?.some((v) => v.selected)) {
            value = customField.values.filter((v) => v.selected).map((v) => v.value);
          } else {
            value = customField.value || [];
          }
        } else if (customField.type === 'select') {
          const selected = customField.values?.find((v) => v.selected)?.value;
          value = selected ?? customField.value ?? '';
        } else {
          value = customField.value ?? '';
        }
        return {
          ...customField,
          form_item_id: customField.key,
          value,
          required: match?.required ?? customField.required ?? false,
        };
      })
      .sort((a, b) => a.position - b.position);
  }
  return updatedCustomFields;
}

const DEFAULT_INITIAL_MODEL_FORM_ITEMS = [];

const NewEditContactModal = ({
  contact = {},
  organizationId,
  initialModelFormItems = DEFAULT_INITIAL_MODEL_FORM_ITEMS,
  onCancel,
  setIsModalVisible,
  showToast,
  onContactUpdate = null,
  associateToSiteID,
  handleAddContact = () => {},
  setCurrentContact = undefined,
  setIsNewContact = undefined,
  setIsUnsavedChanges,
  closeModal,
}) => {
  const initialState = {
    name: '',
    phone: '',
    company: '',
    contact_type: 'Grower',
    note: '',
    client_unique_id: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip_code: '',
    email: '',
    custom_fields: initialModelFormItems
      ? initialModelFormItems.map((item, index) => {
        return {
          key: item.key,
          type: item.type,
          label: item.label,
          position: index + 1,
          value: item.type === 'checkbox-group' ? [] : '',
          form_item_id: item.key,
          values: (item.values || []).map((v) => ({ ...v, selected: false })),
          required: item.required,
        };
      })
      : [],
  };
  const [contactData, setContactData] = useState(initialState);
  const [initialContactData, setInitialContactData] = useState(cloneDeep(initialState)); // cloneDeep to avoid mutation during custom field initialization

  const [isNameValid, setIsNameValid] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const [errorMessage, setErrorMessage] = useState({});
  const [shouldScrollToError, setShouldScrollToError] = useState(false);
  const triggerCustomFieldErrorScroll = useCustomFieldErrorScroll(setShouldScrollToError);

  const validateRequiredFields = (data) => {
    const errors = {};

    if (!data.name) {
      errors.name = 'Contact name is required';
    }

    data.custom_fields?.forEach((field) => {
      const isEmpty = Array.isArray(field.value)
        ? field.value.length === 0
        : field.value === '' || field.value == null;

      if (field.required && isEmpty) {
        errors[`custom_fields.${field.label}`] = `${field.label} is required`;
      }
    });

    return errors;
  };

  const clearFieldError = (fieldLabel) => {
    setErrorMessage((prev) => {
      const newErrors = { ...prev };
      delete newErrors[`custom_fields.${fieldLabel}`];
      return newErrors;
    });
  };

  const onClick = () => {
    const errors = validateRequiredFields(contactData);
    setErrorMessage(errors);

    if (Object.keys(errors).length > 0) {
      const hasTopLevelErrors = Object.keys(errors).some((key) => !key.startsWith('custom_fields.'));
      triggerCustomFieldErrorScroll(hasTopLevelErrors);
      return;
    }

    // Here we check for required inputs, name is the only required for contacts
    if (contactData.name) {
      const apiRequest = contact.id ? updateContact : createContact;
      const payload = {
        ...contactData,
        custom_fields: contactData.custom_fields.map((field) => ({
          ...field,
          value:
            field.type === 'checkbox-group' && Array.isArray(field.value) ? JSON.stringify(field.value) : field.value,
        })),
      };
      setIsSaving(true);
      apiRequest({ contact: payload }, organizationId, contact.id)
        .then((response) => {
          if (response.status === 200) {
            return response.json().then((data) => {
              const updatedCustomFields = parseCustomFields(initialModelFormItems, data.contact.custom_fields);
              onContactUpdate?.({ ...data.contact, custom_fields: updatedCustomFields });
              closeModal();
              const contactId = contact.id || data?.contact?.id;
              const successMessage = contact.id ? (
                'Contact has been updated'
              ) : (
                <span>
                  Contact has been saved.{' '}
                  <a href={`/contacts/${contactId}`} className="underline inline">
                    View contact.
                  </a>
                </span>
              );
              showToast({ message: successMessage, type: 'success' });
              return data;
            });
          }
          return response.json();
        })
        .then((data) => {
          if (data && !data.contact && Array.isArray(data)) {
            data.forEach((error) => showToast({ message: error?.message || String(error), type: 'error' }));
            return;
          }
          if (!data?.contact) {
            return;
          }
          if (associateToSiteID) {
            // This part is for Create contact on site details page
            addContactsToSite(
              { organization_id: data.contact.organization_id, contacts: [data.contact] },
              associateToSiteID,
            )
              .then(() => {
                handleAddContact(data.contact);
              })
              .then(() => {
                setIsModalVisible(false);
              })
              .catch((error) => {
                showToast({ message: error.message, type: 'error' });
              });
          } else {
            // update the current contact in the parent component if the prop is passed
            if (setCurrentContact) {
              setCurrentContact(data.contact);
            }
            // update the isNewContact state in the parent component if the prop is passed to update the contacts list
            if (setIsNewContact) {
              setIsNewContact(true);
            }
            setIsModalVisible(false);
          }
        })
        .catch((error) => {
          showToast({ message: error?.message || String(error), type: 'error' });
        })
        .finally(() => {
          setIsSaving(false);
          setIsUnsavedChanges(false);
        });
    }
  };
  useEffect(() => {
    if (contact.id) {
      getContact(organizationId, contact.id)
        .then((resp) => resp.json())
        .then((data) => {
          const updatedCustomFields = parseCustomFields(initialModelFormItems, data.contact.custom_fields);

          setContactData({
            ...data.contact,
            custom_fields: updatedCustomFields,
            name: data.contact.name || '',
            phone: data.contact.phone || '',
            company: data.contact.company || '',
            contact_type: data.contact.contact_type || '',
            note: data.contact.note || '',
            address1: data.contact.address1 || '',
            address2: data.contact.address2 || '',
            city: data.contact.city || '',
            state: data.contact.state || '',
            zip_code: data.contact.zip_code || '',
            email: data.contact.email || '',
          });
          setInitialContactData({
            ...data.contact,
            custom_fields: cloneDeep(updatedCustomFields), // cloneDeep to avoid mutation during custom field updates
            name: data.contact.name || '',
            phone: data.contact.phone || '',
            company: data.contact.company || '',
            contact_type: data.contact.contact_type || '',
            note: data.contact.note || '',
            address1: data.contact.address1 || '',
            address2: data.contact.address2 || '',
            city: data.contact.city || '',
            state: data.contact.state || '',
            zip_code: data.contact.zip_code || '',
            email: data.contact.email || '',
          });
        });
    }
  }, [contact.id, initialModelFormItems, organizationId]);

  // check if there are unsaved changes
  useEffect(() => {
    setIsUnsavedChanges(!isEqual(contactData, initialContactData));
  }, [contactData, initialContactData, setIsUnsavedChanges]);

  return (
    <div className="h-fit mx-auto max-w-4xl vamos-white">
      <div className="overflow-y-auto max-h-[65vh]">
        <MainInputs
          contactData={contactData}
          setContactData={setContactData}
          isNameValid={isNameValid}
          setIsNameValid={setIsNameValid}
          errorMessage={errorMessage}
        />

        {contactData.custom_fields && (
        <div className="mt-6 px-5.5">
          <CustomFieldsForm
            isSingleColForContacts
            customFields={contactData.custom_fields}
            setData={setContactData}
            errorMessage={errorMessage}
            shouldScrollToError={shouldScrollToError}
            customFieldMaxWidths={{ date: 'w-full' }}
            isNumberFieldNegativeAllowed={false}
            clearFieldError={clearFieldError}
          />
        </div>
        )}
      </div>
      <div className="border-t py-5 border-vamos-blue-gray flex space-x-4 justify-end px-5.5">
        <Button label="Cancel" size="md" color="secondary" className="px-6" onClick={onCancel} />
        <Button
          label={contact.id ? 'Update' : 'Create'}
          size="md"
          onClick={onClick}
          isDisabled={isSaving}
          dataTestId={contact.id ? 'update-contact-button' : 'create-contact-button'}
        />
      </div>
    </div>
  );
};

NewEditContactModal.propTypes = {
  contact: PropTypes.object,
  organizationId: PropTypes.number.isRequired,
  initialModelFormItems: PropTypes.array,
  onCancel: PropTypes.func.isRequired,
  showToast: PropTypes.func.isRequired,
  onContactUpdate: PropTypes.func,
  associateToSiteID: PropTypes.number,
  handleAddContact: PropTypes.func,
  setCurrentContact: PropTypes.func,
  setIsNewContact: PropTypes.func,
  setIsModalVisible: PropTypes.func,
  setIsUnsavedChanges: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
};

export default NewEditContactModal;
